import { useEffect, useState } from "react";
import { api, brl, formatApiErrorDetail } from "@/lib/api";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Bell, Plus, Trash2, Send, Sparkles, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [states, setStates] = useState([]);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", vehicle_type: "all", uf: "all", only_25_30: false, brand: "", min_price: "", max_price: "", min_score: 70 });
  const [sending, setSending] = useState(null);

  const load = () => api.get("/alerts").then((r) => setAlerts(r.data.alerts));
  useEffect(() => {
    load();
    api.get("/meta/states").then((r) => setStates(r.data.states));
    api.get("/meta/vehicle-types").then((r) => setTypes(r.data.types));
  }, []);

  const submit = async () => {
    try {
      await api.post("/alerts", {
        name: form.name || "Meu alerta",
        vehicle_type: form.vehicle_type,
        uf: form.uf,
        only_25_30: form.only_25_30,
        brand: form.brand || null,
        min_price: form.min_price ? Number(form.min_price) : null,
        max_price: form.max_price ? Number(form.max_price) : null,
        min_score: Number(form.min_score),
      });
      toast.success("Alerta criado");
      setOpen(false);
      setForm({ name: "", vehicle_type: "all", uf: "all", only_25_30: false, brand: "", min_price: "", max_price: "", min_score: 70 });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const send = async (id) => {
    setSending(id);
    try {
      const { data } = await api.post(`/alerts/${id}/send`);
      if (data.sent) toast.success(`Email enviado com ${data.matches} oportunidades${data.mocked ? " (modo teste)" : ""}`);
      else toast.info(data.reason || "Sem correspondências");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSending(null); }
  };

  const remove = async (id) => {
    await api.delete(`/alerts/${id}`);
    toast.success("Alerta removido");
    load();
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-3">
              <Bell className="w-6 h-6 text-emerald-400" />
              <h1 className="font-display text-3xl font-bold text-white">Alertas por Email</h1>
            </div>
            <p className="text-slate-400 text-sm mt-1">Salve filtros e receba um email quando novos lotes baterem.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold" data-testid="new-alert-btn">
                <Plus className="w-4 h-4 mr-2" />Novo alerta
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
              <DialogHeader><DialogTitle>Criar alerta</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Nome do alerta</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: SUVs em SP baratos" className="mt-1.5 bg-slate-950 border-slate-800" data-testid="alert-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Tipo</Label>
                    <Select value={form.vehicle_type} onValueChange={(v) => setForm({ ...form, vehicle_type: v })}>
                      <SelectTrigger className="mt-1.5 bg-slate-950 border-slate-800"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="all">Todos</SelectItem>
                        {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Estado</Label>
                    <Select value={form.uf} onValueChange={(v) => setForm({ ...form, uf: v })}>
                      <SelectTrigger className="mt-1.5 bg-slate-950 border-slate-800"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800 max-h-64">
                        <SelectItem value="all">Todos</SelectItem>
                        {states.map((s) => <SelectItem key={s.uf} value={s.uf}>{s.uf}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Marca/Modelo</Label>
                  <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Ex: Corolla" className="mt-1.5 bg-slate-950 border-slate-800" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Preço mín</Label>
                    <Input type="number" value={form.min_price} onChange={(e) => setForm({ ...form, min_price: e.target.value })} className="mt-1.5 bg-slate-950 border-slate-800" />
                  </div>
                  <div>
                    <Label>Preço máx</Label>
                    <Input type="number" value={form.max_price} onChange={(e) => setForm({ ...form, max_price: e.target.value })} className="mt-1.5 bg-slate-950 border-slate-800" />
                  </div>
                  <div>
                    <Label>Score mín</Label>
                    <Input type="number" value={form.min_score} onChange={(e) => setForm({ ...form, min_score: e.target.value })} className="mt-1.5 bg-slate-950 border-slate-800" />
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <Switch checked={form.only_25_30} onCheckedChange={(v) => setForm({ ...form, only_25_30: v })} />
                  <span className="text-sm text-slate-200">Apenas 25% + 30 boletos</span>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-700">Cancelar</Button>
                <Button onClick={submit} className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold" data-testid="create-alert-submit">Criar alerta</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mt-6 space-y-3">
          {alerts.length === 0 ? (
            <div className="p-8 rounded-xl border border-dashed border-slate-800 text-center text-slate-400 text-sm">
              Nenhum alerta ainda. Crie o primeiro para receber oportunidades no seu email.
            </div>
          ) : alerts.map((a) => (
            <div key={a._id} className="p-4 rounded-xl bg-slate-900/70 border border-slate-800 flex flex-wrap items-center justify-between gap-3" data-testid={`alert-row-${a._id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-emerald-400" />
                  <h3 className="font-semibold text-white">{a.name}</h3>
                </div>
                <div className="mt-1 text-xs font-mono text-slate-400 flex flex-wrap gap-x-3">
                  {a.vehicle_type && a.vehicle_type !== "all" && <span>tipo: {a.vehicle_type}</span>}
                  {a.uf && a.uf !== "all" && <span>UF: {a.uf}</span>}
                  {a.brand && <span>marca: {a.brand}</span>}
                  {a.min_price && <span>min: {brl(a.min_price)}</span>}
                  {a.max_price && <span>max: {brl(a.max_price)}</span>}
                  <span>score ≥ {a.min_score}</span>
                  {a.only_25_30 && <span className="text-purple-300">25%+30x</span>}
                </div>
                {a.last_sent_at && (
                  <div className="text-[10px] text-slate-500 mt-1">Último envio: {new Date(a.last_sent_at).toLocaleString("pt-BR")} · {a.last_matches} lotes</div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => send(a._id)} disabled={sending === a._id} className="border-emerald-700/50 text-emerald-300 hover:bg-emerald-500/10" data-testid={`send-alert-${a._id}`}>
                  {sending === a._id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1.5" />Enviar agora</>}
                </Button>
                <Button size="sm" variant="outline" onClick={() => remove(a._id)} className="border-slate-700 text-slate-300 hover:bg-red-500/10 hover:text-red-300 hover:border-red-500/40" data-testid={`delete-alert-${a._id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 p-4 rounded-lg bg-slate-900/50 border border-slate-800 text-xs text-slate-400 flex items-start gap-2">
          <Mail className="w-4 h-4 mt-0.5 text-emerald-400" />
          <div>
            <strong className="text-slate-200">Como funciona:</strong> Emails são enviados via Resend do endereço <code className="font-mono text-emerald-400">onboarding@resend.dev</code>. No modo grátis da Resend, apenas o email cadastrado na sua conta Resend recebe efetivamente — para enviar a qualquer endereço, verifique um domínio próprio em resend.com/domains.
          </div>
        </div>
      </div>
    </div>
  );
}
